
// Global TTS Controller to prevent overlapping audio
// Ensures only ONE session is active at a time.

interface TTSSession {
    id: number;
    controller?: AbortController;
    audioElement?: HTMLAudioElement | AudioBufferSourceNode; // Or wrapper
    provider?: string;
    context?: string; // 'reader' | 'chat' | 'system'
}

let activeSession: TTSSession = { id: 0 };
let currentAudio: HTMLAudioElement | null = null;
// We also track WebSpeech synthesis separately because it's global
const synth = window.speechSynthesis;

// Stop Latch to prevent late callbacks from triggering actions after a stop
let stopToken = 0;
let lastStopSessionId = 0;

export function getStopToken() {
    return stopToken;
}

function incrementStopToken(reason: string, sessionId: number) {
    stopToken++;
    lastStopSessionId = sessionId;
    logTtsEvent('STOP_LATCHED', { reason, stopToken, sessionId });
}

export function startSession(context: string = 'unknown'): number {
    // 1. Stop any valid previous sessions but DO NOT LATCH (allow new one)
    stopAll(`Starting new session (${context})`, { latch: false });

    // 2. Increment ID
    const newId = (activeSession.id || 0) + 1;
    const ac = new AbortController();

    activeSession = {
        id: newId,
        controller: ac,
        context
    };

    console.log(`[TTS] Session ${newId} started (ctx=${context})`);
    logTtsEvent('PLAY_START', { sessionId: newId, context });
    return newId;
}

export function getActiveSessionId(): number {
    return activeSession.id;
}

export function isActiveSession(sessionId: number): boolean {
    return activeSession.id === sessionId;
}

export function registerAudioElement(sid: number, audio: HTMLAudioElement) {
    if (!isActiveSession(sid)) {
        // If session is already stale, stop this audio immediately
        audio.pause();
        audio.src = "";
        return;
    }
    // Clean up previous if distinct (should have been cleared by stopAll, but safe check)
    if (currentAudio && currentAudio !== audio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
    }
    currentAudio = audio;
}

export function getAbortSignal(sid: number): AbortSignal | null {
    if (isActiveSession(sid) && activeSession.controller) {
        return activeSession.controller.signal;
    }
    return null;
}

import { updateMediaSession } from './mediaSession';

export function stopAll(reason: string = "unknown", options: { latch?: boolean } = { latch: true }) {
    // Force stop everything
    const oldId = activeSession.id;
    activeSession.id = -1; // Invalidate immediately

    // Only latch if requested (default true)
    // Latching prevents auto-advance or delayed callbacks from resuming
    if (options.latch !== false) {
        incrementStopToken(reason, oldId);
    }

    _internalStop({ ...activeSession, id: oldId }, reason);
    updateMediaSession('paused');
}

export function stopContext(context: string) {
    if (activeSession.context === context) {
        // Mark session as dead immediately so loops stop
        const oldId = activeSession.id;
        activeSession.id = -1; // Invalidate

        // Context stop usually implies user intent or structural stop, so default latch=true is simpler,
        // but let's check intent. Usually stopContext is called by unmount or specific stop.
        // We'll keep default latching here too.
        incrementStopToken(`Stopping context: ${context}`, oldId);

        _internalStop({ ...activeSession, id: oldId }, `Stopping context: ${context}`);
        updateMediaSession('paused');
    } else {
        console.log(`[TTS] stopContext('${context}') ignored; active is '${activeSession.context}'`);
    }
}

function _internalStop(session: TTSSession, reason: string) {
    console.log(`[TTS] Stop: ${reason} (ID: ${session.id})`);

    // NOTE: Latch logic moved to stopAll/stopContext to avoid latching on reset

    // 1. Abort network or async tasks
    if (session.controller) {
        session.controller.abort();
    }

    // 2. Stop HTML Audio
    if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
        currentAudio.src = "";
        currentAudio = null;
    }

    // 3. Stop Web Speech
    if (synth && synth.speaking) {
        synth.cancel();
    }

    // Notify diagnostics
    if ((window as any).__LAST_TTS_EVENT__) {
        try {
            const data = JSON.parse((window as any).__LAST_TTS_EVENT__);
            data.state = "stopped";
            data.stopReason = reason;
            (window as any).__LAST_TTS_EVENT__ = JSON.stringify(data);
        } catch { }
    }

    logTtsEvent('PLAY_STOP', { reason, sessionId: session.id });
}

export interface TtsEvent {
    ts: string;
    type: string;
    details?: any;
}

const ttsLogs: TtsEvent[] = [];

export function logTtsEvent(type: string, details?: any) {
    ttsLogs.unshift({
        ts: new Date().toLocaleTimeString(),
        type,
        details
    });
    if (ttsLogs.length > 50) ttsLogs.pop();
}

export function getTtsLogs() {
    return ttsLogs;
}
